import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { getUser } from "@/lib/api";
import { getClientId } from "./clientId";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Landmark, Lock, Unlock, Terminal as TerminalIcon, ShieldAlert } from "lucide-react";

export default function POSCashRegister() {
  const { terminal, register, refreshRegister } = useOutletContext();
  const me = getUser();
  const canForceRelease = me && ["manager", "superadmin", "administrator", "admin"].includes((me.role || "").toLowerCase());
  const [terminals, setTerminals] = useState([]);
  const [openingFloat, setOpeningFloat] = useState(0);
  const [actualCash, setActualCash] = useState(0);
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const clientId = getClientId();
  const lockedByOther = register && register.locked_by_other;

  useEffect(() => {
    api.get("/pos/terminals").then((r) => setTerminals(r.data || []));
  }, []);

  useEffect(() => {
    if (!terminal) return;
    api.get("/pos/cash-registers", { params: { terminal_id: terminal.id } })
      .then((r) => setHistory(r.data || []));
  }, [terminal, register]);

  const pickTerminal = (id) => {
    const t = terminals.find((x) => x.id === id);
    if (!t) return;
    localStorage.setItem("pos_terminal_id", t.id);
    // Force the shell to re-read — cheapest way is a soft reload of the page.
    window.location.reload();
  };

  const openReg = async () => {
    if (!terminal) return toast.error("Pick a terminal in the top bar first");
    setBusy(true);
    try {
      await api.post("/pos/cash-registers/open", {
        terminal_id: terminal.id, opening_float: Number(openingFloat) || 0, notes, client_id: clientId,
      });
      toast.success("Register opened"); refreshRegister?.();
      setOpeningFloat(0); setNotes("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const closeReg = async () => {
    if (!register) return;
    if (lockedByOther) return toast.error("This terminal is locked to another workstation");
    setBusy(true);
    try {
      const r = await api.post(`/pos/cash-registers/${register.id}/close`, {
        actual_cash: Number(actualCash) || 0, notes, client_id: clientId,
      });
      toast.success(`Register closed · variance ₹${r.data.variance}`);
      refreshRegister?.();
      setActualCash(0); setNotes("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Landmark className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-semibold">Cash Register</h2>
          <span className="text-xs text-slate-500 ml-auto">Terminal: {terminal ? `${terminal.code} · ${terminal.name}` : "—"}</span>
        </div>
        {!terminal ? (
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="cr-no-terminal">
            <TerminalIcon className="h-6 w-6 mb-2 opacity-60" />
            <p className="mb-2 font-semibold">Pick a terminal to open its cash register.</p>
            {terminals.length === 0 ? (
              <p className="text-xs">No terminals set up for this store yet. Go to <a href="/pos/terminals" className="underline font-semibold">Terminals</a> to add one.</p>
            ) : (
              <select
                onChange={(e) => e.target.value && pickTerminal(e.target.value)}
                className="h-9 w-full max-w-xs rounded-md border border-amber-300 text-sm px-2 bg-white text-gray-900"
                data-testid="cr-terminal-picker"
                defaultValue=""
              >
                <option value="">— Pick a terminal —</option>
                {terminals.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
              </select>
            )}
          </div>
        ) : lockedByOther ? (
          <div className="space-y-3">
            <div className="p-6 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900" data-testid="cr-locked">
              Terminal <b>{terminal.code}</b> is already open on another workstation as register <b className="font-mono">{register.reg_no}</b>{register.opened_by_name ? ` by ${register.opened_by_name}` : ""}.<br/>
              You can view history below, but billing / close is disabled here until the owning workstation closes the register.
            </div>
            {canForceRelease && (
              <div className="p-4 border border-red-200 bg-red-50 rounded-md space-y-2" data-testid="cr-force-release-panel">
                <div className="flex items-center gap-2 text-sm text-red-900">
                  <ShieldAlert className="h-4 w-4" />
                  <b>Manager override</b> — force-release this terminal (use only if the owning browser crashed or is unreachable). Closes the register with expected == actual (variance 0) and logs your name to the audit trail.
                </div>
                <Button
                  variant="outline"
                  className="border-red-500 text-red-700 hover:bg-red-100"
                  onClick={async () => {
                    const reason = window.prompt("Reason for force-release (required)?");
                    if (!reason) return;
                    try {
                      await api.post(`/pos/cash-registers/${register.id}/force-release`, null, { params: { reason } });
                      toast.success("Terminal released — re-open the register when ready");
                      refreshRegister?.();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  data-testid="cr-force-release"
                >
                  <ShieldAlert className="h-4 w-4 mr-1" />Force-Release Terminal
                </Button>
              </div>
            )}
          </div>
        ) : !register ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Register is <b>closed</b>. Open with today's opening cash float to start selling.</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs"><span className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Opening Float (₹)</span><Input type="number" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} data-testid="cr-open-float" /></label>
              <label className="text-xs"><span className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Notes</span><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cr-open-notes" /></label>
            </div>
            <Button onClick={openReg} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cr-open"><Unlock className="h-4 w-4 mr-1" />Open Register</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm bg-emerald-50 border border-emerald-200 rounded p-3">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span><b>{register.reg_no}</b> · opened {new Date(register.opened_at).toLocaleString()} · float ₹{register.opening_float}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs"><span className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Actual Cash Counted (₹)</span><Input type="number" value={actualCash} onChange={(e) => setActualCash(e.target.value)} data-testid="cr-close-cash" /></label>
              <label className="text-xs"><span className="text-[11px] uppercase tracking-widest text-slate-500 block mb-1">Notes</span><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cr-close-notes" /></label>
            </div>
            <Button onClick={closeReg} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="cr-close"><Lock className="h-4 w-4 mr-1" />Close Register</Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 text-sm font-semibold">Register History</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2">Reg #</th><th className="px-3 py-2">Opened</th><th className="px-3 py-2">Closed</th>
              <th className="px-3 py-2 text-right">Float</th><th className="px-3 py-2 text-right">Cash Sales</th>
              <th className="px-3 py-2 text-right">Expected</th><th className="px-3 py-2 text-right">Actual</th><th className="px-3 py-2 text-right">Variance</th><th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No registers yet.</td></tr>}
            {history.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{h.reg_no}</td>
                <td className="px-3 py-2 text-xs">{new Date(h.opened_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{h.closed_at ? new Date(h.closed_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{h.opening_float}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{h.cash_sales || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{h.expected_cash || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{h.actual_cash || 0}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${h.variance < 0 ? "text-red-600" : h.variance > 0 ? "text-amber-600" : "text-emerald-700"}`}>₹{h.variance || 0}</td>
                <td className="px-3 py-2 text-xs">
                  {h.status}
                  {h.force_released && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest font-semibold border rounded px-1 py-0.5 bg-red-50 text-red-700 border-red-200" title={h.force_release_reason || ""}>Forced</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
